import { useContext, useEffect, useState } from "react";
import Input from "../../utils/components/Input";
import Button from "../../utils/components/Button";
import NavigationTabs from "../../utils/components/NavigationTabs";
import SelectBox from "../../utils/components/SelectBox";
import NewTaskAssignmentModal from "./components/NewTaskAssignmentModal";
import TaskCancelModal from "./components/TaskCancelModal";
import { BiEditAlt } from "react-icons/bi";
import { FiLock } from "react-icons/fi";
import { auth } from "../../App/routes";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import { AiOutlineClose, AiOutlineFilePdf } from "react-icons/ai";
import { confirmAlert } from "react-confirm-alert";
import AlertModal from "../../utils/components/AlertModal";
import API from "../../utils/API";
import { useProject } from "../../utils/hooks/useProject";
import { Translate } from "react-localize-redux";
import timeStampToDateFormat from "../../utils/helpers/timestampToDateFormat";
import { useCan } from "../../utils/hooks/useCan";
import formatSubFormData from "../../utils/helpers/formatSubFormData";
import getTimezone from "../../utils/helpers/getTimezone";
import formatSavedSubForm from "../../utils/helpers/formatSavedSubForm";
import { RequestContext } from "../../utils/context/RequestContext";
import getLocation from "../../utils/helpers/getLocation";
import Modal from "../../utils/components/Modal";
import FormStartModal from "./components/FormStartModal";
import toastr from "toastr";

const FormsDetail = ({ settings, translate }) => {
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [isNewTaskAssignmentModalOpen, setIsNewTaskAssignmentModalOpen] =
    useState(false);
  const [isTaskCancelModalOpen, setIsTaskCancelModalOpen] = useState(false);
  const [isLock, setIsLock] = useState(false);
  const [selectedRevisionHistory, setSelectedRevisionHistory] = useState([]);
  const [statusSets, setStatusSets] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [selectedRevision, setSelectedRevision] = useState({});
  const [metadata, setMetadata] = useState([]);
  const [formInfo, setFormInfo] = useState({});
  const [editedFormInfo, setEditedFormInfo] = useState({});
  const [subForms, setSubForms] = useState([]);
  const [editedSubForms, setEditedSubForms] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [taskUsers, setTaskUsers] = useState([]);
  const [taskCompanies, setTaskCompanies] = useState([]);
  const [taskGroups, setTaskGroups] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const [versions, setVersions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [isLatestVersion, setIsLatestVersion] = useState(false);

  const [isAttachmentsUpdated, setIsAttachmentsUpdated] = useState(false);

  const [isSavingForm, setIsSavingForm] = useState(false);
  const [shouldAskLocationAccess, setShouldAskLocationAccess] = useState(false);
  const [userLocation, setUserLocation] = useState(null);

  const [isCreateOptionsModalOpen, setIsCreateOptionsModalOpen] =
    useState(false);
  const [isWorkflowCreateModalOpen, setIsWorkflowCreateModalOpen] =
    useState(false);
  const [isWorkflowStartSending, setIsWorkflowStartSending] = useState(false);

  const [isTaskDetailsFetched, setIsTaskDetailsFetched] = useState(false);
  const [workflowTemplates, setWorkflowTemplates] = useState([]);
  const [selectedMetadata, setSelectedMetadata] = useState([]);

  const { id: formId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { project } = useProject();

  const can = useCan();

  const { setRequestLoading } = useContext(RequestContext);

  useEffect(() => {
    const localOldForm = JSON.parse(localStorage.getItem("old_form"));
    if (localOldForm) {
      setSelectedRevisionHistory(localOldForm.selectedRevisionHistory);
      setStatusSets(localOldForm.statusSets);
      setSelectedStatus(localOldForm.selectedStatus);
      setRevisions(localOldForm.revisions);
      setSelectedRevision(localOldForm.selectedRevision);
      setMetadata(localOldForm.metadata);
      setFormInfo(localOldForm.formInfo);
      setEditedFormInfo(localOldForm.editedFormInfo);
      setSubForms(localOldForm.subForms);
      setEditedSubForms(localOldForm.editedSubForms);
      setSelectedLocation(localOldForm.selectedLocation);
      setAttachments(localOldForm.attachments);
      setVersions(localOldForm.versions);
      setLogs(localOldForm.logs);
      setIsLatestVersion(localOldForm.isLatestVersion);
      setIsAttachmentsUpdated(localOldForm.isAttachmentsUpdated);
      setIsReadOnly(false);

      localStorage.removeItem("old_form");
    } else {
      API.getData(`form/${formId}?es=true&project_id=${project?.id}`)
        .then(({ data: res }) => {
          const { data } = res;

          const formInfo = {
            id: data.id,
            name: data.name,
            no: data.no,
          };

          setIsLock(data.locked);

          setSelectedLocation({
            path: data?.location_path,
            set_id: data.site_set_id,
            id: data.site_set_detail_id,
          });

          setFormInfo(formInfo);
          setEditedFormInfo(formInfo);

          setRevisions(
            data.revision_set.map((revision) => ({
              value: revision.id,
              label: revision.name,
              isSelected: revision.selected,
            })),
          );

          const selectedRevision = data.revision_set.find(
            (revision) => revision.selected,
          );
          if (selectedRevision)
            setSelectedRevision({
              value: selectedRevision.id,
              label: selectedRevision.name,
            });

          setStatusSets(
            data.status_set.map((status) => ({
              value: status.id,
              label: status.name,
              isDisabled: status.locked,
              isSelected: status.selected,
            })),
          );

          if (data?.status?.id && data?.status?.name) {
            setSelectedStatus([{
              value: data.status.id,
              label: data.status.name,
            }]);
          }

          const headers = data.headers.map((header) => ({
            id: header.id,
            name: header.name,
            data: header.data.map((data) => ({
              value: data.id,
              label: data.name,
              isSelected: data.selected,
            })),
          }));

          setMetadata(JSON.parse(JSON.stringify(headers)));
          setSelectedMetadata(JSON.parse(JSON.stringify(headers)));

          if (data.attachments && data.attachments.length > 0) {
            setAttachments(
              data.attachments.map((attachment) => {
                return {
                  name: attachment.file_name,
                  uploadedBy: attachment.created_name,
                  uploadedAt: timeStampToDateFormat(attachment.created_at),
                  id: attachment.id,
                  url: attachment.file_path,
                };
              }),
            );
          }

          const newSubForm = formatSavedSubForm(
            res.data.sub_forms,
            null,
            setShouldAskLocationAccess,
          );

          setSubForms(JSON.parse(JSON.stringify(newSubForm)));
          setEditedSubForms(JSON.parse(JSON.stringify(newSubForm)));

          setIsReadOnly(true);

          API.getData(`forms/versions/${data.original_id}`).then(
            ({ data: res }) => {
              setVersions(
                res.data.map((version) => ({
                  id: version.id,
                  value: version.version,
                  status: version.status,
                  modified_at: version.modified_at,
                  isLatest: version.latest,
                })),
              );
            },
          );

          API.getData(`form/logs/${data.original_id}`).then(({ data: res }) => {
            setLogs(
              res.data.map((log) => ({
                eventType: log.event_type,
                user: log.event_user,
                eventDetail: log.event_detail,
                formVersion: log.event_version,
                dateAndTime: timeStampToDateFormat(log.event_date),
                eventData: log.event_data,
              })),
            );
          });
        })
        .then(() => {
          setIsSavingForm(false);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  useEffect(() => {
    if (versions.length) {
      const latestVersion = versions.find((version) => version.isLatest);
      const currentVersion = versions.find(
        (version) => String(version.id) === String(formId),
      );

      if (currentVersion) {
        setSelectedRevisionHistory({
          value: currentVersion.id,
          label: `#${currentVersion.value} - ${timeStampToDateFormat(
            currentVersion.modified_at,
          )}`,
        });
      }

      if (latestVersion && currentVersion) {
        setIsLatestVersion(
          String(latestVersion.id) === String(currentVersion.id),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, versions]);

  useEffect(() => {
    setEditedFormInfo(formInfo);
  }, [formInfo, isReadOnly]);

  useEffect(() => {
    if (project && taskUsers.length < 1 && !isReadOnly)
      API.getData(`task/initialize?project_id=${project?.id}`).then(
        ({ data: res }) => {
          setTaskUsers(
            res.data.users.map((user) => ({
              value: user.user_id,
              label: user.user_name,
              secLabel: user.company_name,
            })),
          );

          setTaskCompanies(
            res.data.companies.map((company) => ({
              value: company.id,
              label: company.name,
              users: company.users,
            })),
          );

          setTaskGroups(
            res.data.groups.map((group) => ({
              value: group.id,
              label: group.name,
              users: group.users,
              secLabel:
                group.users.length > 0 ? group.users[0].company_name : "",
            })),
          );
        },
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnly]);

  useEffect(() => {
    if (shouldAskLocationAccess && !userLocation && !window.chrome) {
      getLocation((location) => setUserLocation(location));
    }
  }, [shouldAskLocationAccess, userLocation]);

  const handleCloseForm = () => {
    if (location.state?.from) window.history.back();
    else navigate(auth.forms.link);
  };

  const handleAddRemoveRepeatableForm = (
    type,
    index,
    subFormIndex,
    repeatableIndex,
  ) => {
    if (type === "add") {
      const newSubForms = [...editedSubForms];
      const newRepeatableFormFields = newSubForms[subFormIndex]
        .fields[index]
        .fields[0]
        .map((field) => ({
          ...field,
          value: field.default_value,
          field_attach: {
            image: "",
            note: "",
            original_forms: [],
          },
        }));

      newSubForms[subFormIndex].fields[index].fields.push(
        newRepeatableFormFields,
      );

      setEditedSubForms(newSubForms);
    } else if (type === "remove") {
      const newSubForms = [...editedSubForms];

      newSubForms[subFormIndex].fields[index].fields.splice(repeatableIndex, 1);

      setEditedSubForms(newSubForms);
    }
  };

  const handleUpdateFormField = (
    value,
    formIndex,
    fieldIndex,
    repeatableIndex,
    repeatableFieldIndex,
  ) => {
    const newSubForms = [...editedSubForms];

    if (repeatableIndex !== null) {
      if (newSubForms[formIndex]?.fields[fieldIndex]?.fields[repeatableIndex]) {
        newSubForms[formIndex].fields[fieldIndex].fields[repeatableIndex][
          repeatableFieldIndex
        ].value = value;

        setEditedSubForms([...newSubForms]);
      }
    } else {
      if (newSubForms[formIndex]?.fields[fieldIndex]) {
        newSubForms[formIndex].fields[fieldIndex].value = value;

        setEditedSubForms([...newSubForms]);
      }
    }
  };

  const handleUpdateFormFieldAttachment = (
    value,
    formIndex,
    fieldIndex,
    repeatableIndex,
    repeatableFieldIndex,
    attachmentType,
  ) => {
    const newSubForms = [...editedSubForms];
    if (repeatableIndex !== null) {
      const targetField = newSubForms?.[formIndex]?.fields?.[fieldIndex]?.fields?.[repeatableIndex]?.[repeatableFieldIndex];

      if (targetField) {
        if (!targetField.field_attach) {
          targetField.field_attach = {};
        }

        targetField.field_attach[attachmentType] = value;
        setEditedSubForms([...newSubForms]);
      }
    } else {
      const targetField = newSubForms?.[formIndex]?.fields?.[fieldIndex];
      if (targetField) {
        if (!targetField.field_attach) {
          targetField.field_attach = {};
        }

        targetField.field_attach[attachmentType] = value;
        setEditedSubForms([...newSubForms]);
      }
    }
  };

  const handleSaveForm = (e, sendData = false, type = null) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    const isInfoUpdated =
      JSON.stringify(formInfo) !== JSON.stringify(editedFormInfo);
    const isFormUpdated =
      JSON.stringify(subForms) !== JSON.stringify(editedSubForms);
    const isStatusUpdated =
      selectedStatus?.value !==
      statusSets?.find((status) => status.isSelected)?.value;
    const isRevisionUpdated =
      selectedRevision?.value !==
      revisions?.find((revision) => revision.isSelected)?.value;

    const isMetadataUpdated =
      JSON.stringify(metadata) !== JSON.stringify(selectedMetadata);

    if (
      isInfoUpdated ||
      isFormUpdated ||
      isStatusUpdated ||
      isRevisionUpdated ||
      isMetadataUpdated ||
      !selectedStatus?.value
    ) {
      if (!selectedStatus?.value || !selectedRevision?.value) {
        const message = !selectedStatus?.value
          ? translate("forms.status_empty_alert")
          : translate("forms.revision_empty_alert");

        confirmAlert({
          customUI: ({ onClose }) => (
            <AlertModal
              buttons={[
                {
                  label: translate("okay"),
                  onClick: () => onClose(),
                  classNames: "btn-primary",
                },
              ]}
              message={message}
            />
          ),
        });
        return;
      }

      const headers = selectedMetadata.map((metadata) => ({
        metadata_set_id: metadata.id,
        details: metadata.data
          .filter((data) => data.isSelected)
          .map((data) => data.value),
      }));

      const headersCheck = headers.filter((header) => header.details.length);

      if (headersCheck.length !== headers.length) {
        confirmAlert({
          customUI: ({ onClose }) => (
            <AlertModal
              buttons={[
                {
                  label: translate("okay"),
                  onClick: () => onClose(),
                  classNames: "btn-primary",
                },
              ]}
              message={translate("forms.metadata_empty_alert")}
            />
          ),
        });
        return;
      }

      setIsSavingForm(true);
      setRequestLoading(true);

      const formData = {
        name: editedFormInfo.name,
        status_set_detail_id: selectedStatus?.value,
        revision_set_detail_id: selectedRevision?.value,
        headers,
        sub_forms: formatSubFormData(editedSubForms),
      };

      API.putData(
        `form/${formInfo.id}?project_id=${project?.id}`,
        formData,
      ).then(({ data: res }) => {
        if (isAttachmentsUpdated && attachments.length) {
          API.putData(
            `form_attachment/${res.data.id}?project_id=${project.id}`,
            {
              files: attachments
                .filter((attachment) => attachment.base64)
                .map((attachment) => ({
                  file_data: attachment.base64,
                  file_name: attachment.name,
                })),
              prev_form_id: formId,
            },
          ).then(() => {
            handlePostSubmit(sendData, res.data.id, type, res?.message);
          }).catch(() => {
            setIsSavingForm(false);
            setRequestLoading(false);
          });
        } else {
          handlePostSubmit(sendData, res.data.id, type, res?.message);
        }

        localStorage.removeItem("nested_form");
        localStorage.removeItem("new_form_location_sets");
      }).catch(() => {
        setIsSavingForm(false);
        setRequestLoading(false);
      });
    } else {
      if (isAttachmentsUpdated && attachments.length) {
        API.putData(`form_attachment/${formInfo.id}?project_id=${project.id}`, {
          files: attachments
            .filter((attachment) => attachment.base64)
            .map((attachment) => ({
              file_data: attachment.base64,
              file_name: attachment.name,
            })),
          prev_form_id: formId,
        }).catch(() => { });
      }

      if (type) {
        setIsSavingForm(true);
        setRequestLoading(true);

        handlePostSubmit(sendData, formInfo.id, type);
      } else {
        setIsReadOnly(!isReadOnly);
      }
    }
  };

  const handlePostSubmit = (sendData, id, type) => {
    if (!sendData) {
      setIsSavingForm(false);
      navigate(auth.formDetails.link.replace(":id", id));
    } else {
      if (type === "task") {
        sendData.form_id = id;
        // Ensure loading stays visible for the POST /tasks request
        setRequestLoading(true);
        API.postData("tasks", sendData)
          .then(({ data: res }) => {
            toastr.clear();
            toastr.success(res.message);

            localStorage.removeItem("nested_form");
            localStorage.removeItem("new_form_location_sets");

            navigate(auth.formBoxDetails.link.replace(":id", res.data.id));
          })
          .catch(() => {
            setIsSavingForm(false);
            setRequestLoading(false);
          });
      } else if (type === "workflow-start") {
        setIsWorkflowStartSending(true);
        sendData.form_id = id;
        // Ensure loading stays visible for the POST /workflow/start request
        setRequestLoading(true);
        API.postData("workflow/start", sendData)
          .then(({ data: res }) => {
            toastr.clear();
            toastr.success(res.message);

            localStorage.removeItem("nested_form");
            localStorage.removeItem("new_form_location_sets");

            navigate(auth.formDetails.link.replace(":id", id));

            setIsSavingForm(false);
            setIsWorkflowStartSending(false);

            setIsWorkflowCreateModalOpen(false);
            setIsReadOnly(!isReadOnly);
          })
          .catch((err) => {
            toastr.error(err.response.data.message);

            setIsSavingForm(false);
            setIsWorkflowStartSending(false);
            setRequestLoading(false);
          });
      }
    }
  };

  const handleKeepOldForm = () => {
    const oldForm = {
      selectedRevisionHistory,
      statusSets,
      selectedStatus,
      revisions,
      selectedRevision,
      metadata,
      formInfo,
      editedFormInfo,
      subForms,
      editedSubForms,
      selectedLocation,
      attachments,
      versions,
      logs,
      isLatestVersion,
      isAttachmentsUpdated,
    };

    localStorage.setItem("old_form", JSON.stringify(oldForm));
  };

  const handleExportPDF = () => {
    const timezone = getTimezone();

    if (timezone)
      API.getData(
        `form/${formId}/export?file_format=pdf&timezone=${timezone}`,
      ).then(({ data: res }) => {
        const link = document.createElement("a");
        link.href = res.data;
        link.setAttribute("download", `${formInfo.name}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
  };

  useEffect(() => {
    if (
      project &&
      !isTaskDetailsFetched &&
      (isCreateOptionsModalOpen ||
        isNewTaskAssignmentModalOpen ||
        isWorkflowCreateModalOpen)
    ) {
      if (can.doThat("workflow:read"))
        API.getData(
          `workflow_templates?project_id=${project?.id}&page=1&size=1000`,
        ).then(({ data: res }) => {
          setWorkflowTemplates(res.data);
          setIsTaskDetailsFetched(true);
        });

      if (can.doThat("task:create"))
        API.getData(`task/initialize?project_id=${project?.id}`).then(
          ({ data: res }) => {
            const users = res.data.users.map((user) => ({
              isActionUser: user.is_action_user,
              isInfoUser: user.is_info_user,
              value: user.user_id,
              label: user.user_name,
              secLabel: user.company_name,
            }));

            setTaskUsers(users);

            const companies = res.data.companies.map((company) => ({
              value: company.id,
              label: company.name,
              users: company.users,
            }));

            setTaskCompanies(companies);

            const groups = res.data.groups.map((group) => ({
              value: group.id,
              label: group.name,
              users: group.users.map((user) => ({
                ...user,
                isActionUser: user.is_action_user,
                isInfoUser: user.is_info_user,
              })),
              secLabel:
                group.users.length > 0 ? group.users[0].company_name : "",
            }));

            setTaskGroups(groups);

            setIsTaskDetailsFetched(true);
          },
        );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCreateOptionsModalOpen,
    isNewTaskAssignmentModalOpen,
    isWorkflowCreateModalOpen,
  ]);

  const canCreateTask = can.doThat("task:create");
  const canCreateWorkflow = can.doThat("workflow:create");

  return (
    <div className="FormsDetail form-page">
      <div className="container bg-white shadow-sm border border-1 rounded">
        {/** Form actions */}
        <div
          className={`row flex-column-reverse flex-lg-row mt-4 pb-4 border-bottom border-2 ${isReadOnly ? "d-none" : "d-flex"
            }`}
        >
          <div className="col-12 col-lg-3">
            <Button
              className="btn-secondary px-5 ms-0 ms-lg-4 mt-2 mt-lg-0"
              onClick={() => {
                setIsReadOnly(!isReadOnly);
              }}
              disabled={isSavingForm}
            >
              {translate("cancel")}
            </Button>
          </div>
          <div className="col-12 col-lg-4 mt-2 mt-lg-0">
            <SelectBox
              options={statusSets}
              placeholder={translate("forms.place-holders.select-status")}
              value={selectedStatus}
              onChange={(selectedStatus) => setSelectedStatus(selectedStatus)}
              disabled={isReadOnly || isSavingForm}
            />
          </div>
          <div className="col-12 col-lg-5 d-flex justify-content-end">
            <Button
              className="btn-orange text-white px-5"
              onClick={handleSaveForm}
              disabled={isSavingForm}
            >
              {translate("save")}
            </Button>

            <Button
              className="btn-orange text-white px-5 ms-5"
              onClick={() => {
                if (canCreateTask && canCreateWorkflow)
                  setIsCreateOptionsModalOpen(!isCreateOptionsModalOpen);
                else if (canCreateTask) setIsNewTaskAssignmentModalOpen(true);
                else if (canCreateWorkflow) setIsWorkflowCreateModalOpen(true);
              }}
              disabled={isSavingForm}
            >
              {translate("send")}
            </Button>

            <Translate>
              {({ translate }) => (
                <>
                  {(canCreateTask || canCreateWorkflow) && (
                    <Modal
                      show={isCreateOptionsModalOpen}
                      setShow={() =>
                        setIsCreateOptionsModalOpen(!isCreateOptionsModalOpen)
                      }
                    >
                      <div className="d-flex align-items-center justify-content-around">
                        {canCreateTask && (
                          <Button
                            className="btn-secondary px-5"
                            onClick={() => {
                              setIsNewTaskAssignmentModalOpen(
                                !isNewTaskAssignmentModalOpen,
                              );
                              setIsCreateOptionsModalOpen(
                                !isCreateOptionsModalOpen,
                              );
                            }}
                          >
                            {translate("forms.create-task")}
                          </Button>
                        )}
                        {canCreateWorkflow && (
                          <Button
                            className="btn-secondary px-5"
                            onClick={() => {
                              setIsWorkflowCreateModalOpen(
                                !isWorkflowCreateModalOpen,
                              );
                              setIsCreateOptionsModalOpen(
                                !isCreateOptionsModalOpen,
                              );
                            }}
                          >
                            {translate("forms.create-workflow")}
                          </Button>
                        )}
                      </div>
                    </Modal>
                  )}
                  {canCreateTask && (
                    <NewTaskAssignmentModal
                      show={isNewTaskAssignmentModalOpen}
                      hideModal={(action) => {
                        if (action === "send") {
                          setIsReadOnly(!isReadOnly);
                          setIsLock(!isLock);
                        }
                        setIsNewTaskAssignmentModalOpen(
                          !isNewTaskAssignmentModalOpen,
                        );
                      }}
                      users={taskUsers}
                      companies={taskCompanies}
                      groups={taskGroups}
                      formId={parseInt(formId)}
                      translate={translate}
                      handleSaveForm={(sendData, type) =>
                        handleSaveForm(null, sendData, type)
                      }
                    />
                  )}
                  {canCreateWorkflow && (
                    <FormStartModal
                      show={isWorkflowCreateModalOpen}
                      hideModal={() =>
                        setIsWorkflowCreateModalOpen(!isWorkflowCreateModalOpen)
                      }
                      translate={translate}
                      handleSaveForm={(sendData, type) =>
                        handleSaveForm(null, sendData, type)
                      }
                      users={taskUsers}
                      workflowTemplates={workflowTemplates}
                      template={formId}
                      isSendingFromParent={isWorkflowStartSending}
                      isSendingForm={isSavingForm}
                    />
                  )}
                </>
              )}
            </Translate>
          </div>
        </div>
        {/** Form actions */}

        {/** Form Edit */}
        <div
          className={`row flex-column-reverse flex-lg-row align-items-center justify-content-end mt-4 pb-4 border-bottom border-2
      ${isReadOnly ? "d-flex" : "d-none"}`}
        >
          <div className="col-12 col-lg-4 mt-2 mt-lg-0">
            <p className="fs-3 text-orange m-0">{formInfo?.name}</p>
          </div>
          <div className="col-12 col-lg-4 mt-2 mt-lg-0 d-flex flex-column align-items-center">
            <p className="fs-4 fw-bold mb-2">{formInfo?.no}</p>
            <p className="fs-4 border border-2 p-2 rounded text-center mb-0">
              {selectedStatus[0]?.label}
            </p>
          </div>
          <div className="col-12 col-lg-4 d-flex justify-content-end align-items-center">
            <Button
              className="border-0 bg-transparent shadow-none p-0 mx-2"
              onClick={handleExportPDF}
            >
              <AiOutlineFilePdf className="text-red fs-3" />
            </Button>
            {!isLock &&
              (isLatestVersion || !versions.length) &&
              can.doThat("form:update") && (
                <Button
                  className="border-0 bg-transparent shadow-none p-0 mx-2"
                  onClick={() => {
                    setIsReadOnly(!isReadOnly);
                  }}
                >
                  <BiEditAlt className="fs-2 fw-bold" />
                </Button>
              )}
            <Button
              className="border-0 bg-transparent shadow-none p-0 mx-2"
              onClick={handleCloseForm}
            >
              <AiOutlineClose className="fs-4 fw-bold" />
            </Button>
            <SelectBox
              options={versions.map((item) => ({
                value: item.id,
                label: `#${item.value} - ${timeStampToDateFormat(
                  item.modified_at,
                )}`,
              }))}
              value={selectedRevisionHistory}
              onChange={(selectedRevisionHistory) =>
                navigate(
                  auth.formDetails.link.replace(
                    ":id",
                    selectedRevisionHistory.value,
                  ),
                )
              }
              placeholder={translate("forms.revision-history")}
              isSearchable={false}
              isClearable={false}
            />
          </div>
          <div
            className={`col-12 col-lg-4 align-items-center justify-content-end ${isLock ? "d-flex" : "d-none"
              }`}
          >
            <FiLock className="display-5 me-3" />
          </div>
          <TaskCancelModal
            show={isTaskCancelModalOpen}
            hideModal={() => {
              setIsTaskCancelModalOpen(!isTaskCancelModalOpen);
            }}
            cancelAction={() => {
              setIsTaskCancelModalOpen(!isTaskCancelModalOpen);
              setIsLock(!isLock);
            }}
          />
        </div>
        {/** Form Edit */}
        {!isReadOnly && (
          <div className="d-flex justify-content-between align-items-center mt-4 ps-3 pe-3 border-bottom border-2 pb-3">
            <div className="fs-4 text-orange">{formInfo?.name}</div>
            <div className="fs-4 text-black">{formInfo?.no}</div>
          </div>
        )}
        {/** General Information */}
        <div className="row py-3 mt-4 ps-5">
          <div className="col-12">
            <p className="fs-5 text-orange">
              {translate("forms.headers.general-informations")}
            </p>
          </div>
          <div className="col-12 col-lg-10">
            <div className="row align-items-center">
              <p className="col-12 col-lg-2 mb-0 fw-bold">
                {translate("forms.headers.form_name")}
              </p>
              <div className="col-12 col-lg-10">
                <Input
                  placeholder={translate("forms.headers.form_name")}
                  disabled={isReadOnly}
                  onChange={(e) =>
                    setEditedFormInfo((prevEditedFormInfo) => ({
                      ...prevEditedFormInfo,
                      name: e.target.value,
                    }))
                  }
                  value={isReadOnly ? formInfo?.name : editedFormInfo?.name}
                />
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-10 mt-2">
            <div className="row align-items-center">
              <p className="col-12 col-lg-2 mb-0 fw-bold">
                {translate("forms.headers.revision")}
              </p>
              <div className="col-12 col-lg-10">
                <SelectBox
                  options={revisions}
                  placeholder={translate("forms.place-holders.select-revision")}
                  value={selectedRevision}
                  onChange={(selectedRevision) =>
                    setSelectedRevision(selectedRevision)
                  }
                  disabled={isReadOnly}
                />
              </div>
            </div>
          </div>

          {selectedMetadata?.map((meta, index) => (
            <div key={meta.id} className="col-12 col-lg-10 mt-2">
              <div className="row align-items-center">
                <p className="col-12 col-lg-2 mb-0 fw-bold">{meta.name}</p>
                <div className="col-12 col-lg-10">
                  <SelectBox
                    options={meta.data}
                    placeholder={translate(
                      "forms.place-holders.select-metadata",
                      { metadata: meta.name },
                    )}
                    value={meta.data.find((data) => data.isSelected)}
                    onChange={(value) => {
                      const newMetadata = [...selectedMetadata];

                      newMetadata[index].data = newMetadata[index].data.map(
                        (data) => {
                          let newData = { ...data, isSelected: false };

                          if (newData.value === value.value) {
                            newData.isSelected = true;
                          }

                          return newData;
                        },
                      );

                      setSelectedMetadata(newMetadata);
                    }}
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        {/** General Information */}

        <div className="row mt-4 border-bottom border-2">
          <div className="col-12 d-flex justify-content-end">
            <NavigationTabs
              tabs={auth.formDetails.pages.map((item) => ({
                id: item.id,
                title: item.name,
                link:
                  auth.formDetails.link.replace(":id", formId) +
                  (item.link ? `/${item.link}` : ""),
              }))}
              isUsingOutlet
            />
          </div>
        </div>
        <div className="inner-page mb-5">
          <Outlet
            context={{
              isReadOnly,
              formInfo,
              subForms,
              editedSubForms,
              settings,
              selectedLocation,
              isLock,
              handleAddRemoveRepeatableForm,
              handleUpdateFormField,
              attachments,
              setIsAttachmentsUpdated,
              setAttachments,
              translate,
              logs,
              handleUpdateFormFieldAttachment,
              handleKeepOldForm,
            }}
          />
        </div>
      </div>
    </div>
  );
};
export default FormsDetail;
